/**
 * Which physical phone is this — one row per handset in "Your phones".
 *
 * The devices table used to be keyed on the push token. A token is not a
 * phone: Firebase rotates it, a reinstall gets a new one, a person who
 * declined notifications has none. Each of those made a new row, so the same
 * Galaxy appeared three times in someone's list, two of them "last seen"
 * weeks ago, next to a Stop button that would have silenced nothing.
 *
 * Two identifiers now say which phone is which:
 *
 *  - the **installation id**: a random id the app mints on first launch and
 *    keeps. It survives updates and sign-outs; it does not survive an
 *    uninstall, because nothing an app stores does.
 *  - the **hardware hint**: the platform's own per-app id for the handset
 *    (Android's ANDROID_ID, iOS's identifier-for-vendor), hashed ON THE PHONE
 *    before it is sent, so the raw id never leaves it. It is what recognises
 *    a reinstall. The server hashes it again with the account's id before
 *    storing it, so the stored value says nothing across accounts: the same
 *    phone signed in to two accounts has two unrelated hashes.
 *
 * SHA-256 is written out here rather than imported because the phone has no
 * crypto module (expo-crypto is not a dependency and Hermes has no
 * `crypto.subtle`), and adding a native module for one hash would mean a new
 * native build for every phone. It is pure arithmetic, so it gives the same
 * answer in Hermes and workerd, which is the rule for this package.
 */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function utf8(text: string): Uint8Array {
  // TextEncoder is in Hermes and workerd alike.
  return new TextEncoder().encode(text);
}

/** SHA-256 of a string's UTF-8 bytes, as lowercase hex. */
export function sha256Hex(text: string): string {
  const bytes = utf8(text);
  const bitLength = bytes.length * 8;
  const padded = new Uint8Array(((bytes.length + 9 + 63) >> 6) << 6);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const view = new DataView(padded.buffer);
  // Lengths here are far below 2^32 bits; the high word stays zero.
  view.setUint32(padded.length - 4, bitLength >>> 0);
  view.setUint32(padded.length - 8, Math.floor(bitLength / 0x1_0000_0000));

  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const w = new Uint32Array(64);
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));

  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4);
    for (let i = 16; i < 64; i++) {
      const a = w[i - 15]!;
      const b = w[i - 2]!;
      const s0 = rotr(a, 7) ^ rotr(a, 18) ^ (a >>> 3);
      const s1 = rotr(b, 17) ^ rotr(b, 19) ^ (b >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = [h[0]!, h[1]!, h[2]!, h[3]!, h[4]!, h[5]!, h[6]!, h[7]!];
    for (let i = 0; i < 64; i++) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + s1 + ch + K[i]! + w[i]!) >>> 0;
      const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (s0 + maj) >>> 0;
      hh = g; g = f; f = e; e = (d + t1) >>> 0;
      d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h[0] = (h[0]! + a) >>> 0; h[1] = (h[1]! + b) >>> 0; h[2] = (h[2]! + c) >>> 0; h[3] = (h[3]! + d) >>> 0;
    h[4] = (h[4]! + e) >>> 0; h[5] = (h[5]! + f) >>> 0; h[6] = (h[6]! + g) >>> 0; h[7] = (h[7]! + hh) >>> 0;
  }
  return Array.from(h, (x) => x.toString(16).padStart(8, '0')).join('');
}

/**
 * What the phone sends for its hardware id: never the id itself.
 *
 * Prefixed so the value is DrBell's alone -- the same ANDROID_ID hashed by
 * anybody else's code gives a different answer.
 */
export function hardwareHint(platform: 'ios' | 'android', rawId: string): string {
  return sha256Hex(`drbell.hw.v1:${platform}:${rawId}`);
}

/**
 * What the server stores: the phone's hint, hashed again with the account.
 *
 * Scoped to the account on purpose. A reinstall is recognised within one
 * person's phones, which is all de-duplication needs; nothing stored lets
 * anybody say that two accounts share a handset.
 */
export function storedHardwareHash(userId: string, hint: string): string {
  return sha256Hex(`drbell.hw.user.v1:${userId}:${hint}`);
}

/** The shape of a minted installation id: what the server accepts. */
export const INSTALLATION_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
